import type { KeyboardEvent, ReactNode, RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";

/**
 * Elements that can hold focus without being programmatically made focusable.
 * `[tabindex="-1"]` is excluded on purpose: a container made focusable so it can
 * *receive* focus on presentation must not become a Tab stop as well, or every
 * modal gains a stop that reads as an empty group.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FocusScopeProps {
  readonly children: ReactNode;
  /**
   * A scope can be mounted but inert — a sheet that is animating out still has
   * to render, and trapping focus inside it would strand the keyboard.
   */
  readonly active?: boolean;
  /** Escape. Omitted for a scope the user is not permitted to leave. */
  readonly onDismiss?: () => void;
  /**
   * Where focus lands on presentation. Defaults to the first focusable
   * descendant, and falls back to the container itself when there is none, so
   * focus never stays behind on the surface the scope is covering.
   */
  readonly initialFocus?: RefObject<HTMLElement | null>;
  readonly className?: string;
  readonly testId?: string;
  readonly role?: "dialog" | "alertdialog" | "group";
  readonly ariaModal?: boolean;
  readonly ariaLabel?: string;
  readonly ariaLabelledBy?: string;
  readonly ariaDescribedBy?: string;
}

function focusableWithin(container: HTMLElement): readonly HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.getAttribute("aria-hidden") !== "true",
  );
}

/**
 * Contains keyboard focus, moves focus in on presentation, restores it on
 * dismissal, and maps Escape to dismiss (§8.5).
 *
 * The modal trait is the only thing keeping assistive technology out of the
 * still-mounted surface behind an overlay, so this component pairs the trait
 * with the focus containment rather than leaving them to be wired separately —
 * an overlay with `aria-modal` and no focus trap is worse than neither, because
 * it tells AT the background is unreachable while the keyboard walks into it.
 */
export function FocusScope({
  children,
  active = true,
  onDismiss,
  initialFocus,
  className,
  testId,
  role = "dialog",
  ariaModal = true,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
}: FocusScopeProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (container === null) return;

    const previous = document.activeElement;
    restoreTo.current = previous instanceof HTMLElement ? previous : null;

    const target = initialFocus?.current ?? focusableWithin(container)[0] ?? container;
    target.focus();

    return () => {
      // Restoring on the way out is what makes a sheet a detour rather than a
      // dead end: without it the keyboard resumes at the top of the document
      // and the user has to walk back to the control they opened it from.
      restoreTo.current?.focus();
      restoreTo.current = null;
    };
  }, [active, initialFocus]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!active) return;

      if (event.key === "Escape" && onDismiss !== undefined) {
        event.stopPropagation();
        event.preventDefault();
        onDismiss();
        return;
      }

      if (event.key !== "Tab") return;

      const container = containerRef.current;
      if (container === null) return;
      const focusable = focusableWithin(container);
      if (focusable.length === 0) {
        // Nothing to move between, so Tab must still not escape the scope.
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      const current = document.activeElement;

      if (event.shiftKey && (current === first || current === container)) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [active, onDismiss],
  );

  return (
    <div
      ref={containerRef}
      className={className}
      data-testid={testId}
      role={role}
      aria-modal={ariaModal ? true : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}
